import * as signalR from '@microsoft/signalr';
import { createDebugLog } from '@ydistri/utils';
import { getApiUrl } from '../helpers';
import { getItem, LSKey } from '../store/localStore';

export const debugLogYbotSignalr = createDebugLog('SignalR/ybot');

/**
 * A second hub connection, not `/hubs/app`: the dedicated hub runs the same availability and
 * ownership checks as REST, and `client.ts` reloads the page on reconnect - fatal for a chat
 * mid-answer. This connection rejoins its groups instead.
 */
export const ybotSignalrClient = new signalR.HubConnectionBuilder()
  .withUrl(`${getApiUrl()}/hubs/ybot`, {
    accessTokenFactory: () => getItem(LSKey.TOKEN) ?? '',
    transport: signalR.HttpTransportType.ServerSentEvents,
  })
  .withAutomaticReconnect({
    nextRetryDelayInMilliseconds: () => 5000,
  })
  .configureLogging(signalR.LogLevel.Information)
  .build();

/** Groups to rejoin by hand after a reconnect - a new connection id is in no group. */
const joinedConversations = new Set<string>();

const reconnectListeners = new Set<() => void>();

/**
 * Start, stop, join and leave run one at a time, in order: a stop overtaking a start, or a join
 * landing after its own leave, leaves the connection disagreeing with itself.
 */
let pending: Promise<unknown> = Promise.resolve();

const serialize = <T>(operation: () => Promise<T>): Promise<T> => {
  // Runs whether or not the previous operation succeeded - one failure must not stall the queue.
  const next = pending.then(operation, operation);
  pending = next.catch(() => undefined);
  return next;
};

/** The retry policy never stops on its own; without this ceiling a prompt sent during a long outage waits forever. */
const RECONNECT_WAIT_MS = 30_000;

interface ConnectionWaiter {
  resolve: () => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

const connectionWaiters = new Set<ConnectionWaiter>();

const settleWaiters = (error?: Error): void => {
  const waiting = Array.from(connectionWaiters);
  connectionWaiters.clear();

  waiting.forEach(waiter => {
    clearTimeout(waiter.timer);
    if (error) {
      waiter.reject(error);
    } else {
      waiter.resolve();
    }
  });
};

const whenReconnected = (): Promise<void> =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      if (connectionWaiters.delete(waiter)) {
        reject(new Error('The ybot connection did not come back.'));
      }
    }, RECONNECT_WAIT_MS);

    const waiter: ConnectionWaiter = { resolve, reject, timer };
    connectionWaiters.add(waiter);
  });

// While `Reconnecting` the hub rejects every invocation, so wait for the reconnect instead of
// failing a prompt sent during a blip.
const connect = async (): Promise<void> => {
  if (ybotSignalrClient.state === signalR.HubConnectionState.Connected) {
    return;
  }

  if (ybotSignalrClient.state === signalR.HubConnectionState.Disconnected) {
    await ybotSignalrClient.start();
    return;
  }

  // Connecting, Reconnecting or Disconnecting: SignalR owns the transition and start() would throw.
  await whenReconnected();
};

export const startYbotConnection = (): Promise<void> => serialize(connect);

export const stopYbotConnection = (): Promise<void> =>
  serialize(async () => {
    joinedConversations.clear();
    await ybotSignalrClient.stop();
  });

export const joinYbotConversation = (conversationId: string): Promise<void> =>
  serialize(async () => {
    await connect();
    await ybotSignalrClient.invoke('JoinConversation', conversationId);
    joinedConversations.add(conversationId);
  });

export const leaveYbotConversation = (conversationId: string): Promise<void> =>
  serialize(async () => {
    joinedConversations.delete(conversationId);

    if (ybotSignalrClient.state !== signalR.HubConnectionState.Connected) {
      return;
    }

    await ybotSignalrClient.invoke('LeaveConversation', conversationId);
  });

/** Fires once the groups are rejoined after a reconnect. Returns the unsubscribe. */
export const onYbotReconnected = (listener: () => void): (() => void) => {
  reconnectListeners.add(listener);
  return () => reconnectListeners.delete(listener);
};

ybotSignalrClient.onreconnected(connectionId => {
  debugLogYbotSignalr('Reconnected', connectionId);

  // Released before the rejoin is queued: a waiting caller is already behind it in the queue, and
  // holding until the rejoin ran would make the two wait on each other.
  settleWaiters();

  serialize(async () => {
    for (const conversationId of joinedConversations) {
      await ybotSignalrClient.invoke('JoinConversation', conversationId);
    }
  })
    .then(() => reconnectListeners.forEach(listener => listener()))
    .catch((error: unknown) => debugLogYbotSignalr('Rejoining conversations failed', error));
});

ybotSignalrClient.onclose(error => {
  debugLogYbotSignalr('Closed', error);
  settleWaiters(new Error('The ybot connection closed.'));
});
